import { Box, ScrollArea, useMantineTheme } from '@mantine/core';
import { useState, useRef, useCallback, useMemo, forwardRef, useEffect } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface VirtualListProps<T> {
  items: T[];
  itemHeight: number | ((index: number) => number);
  renderItem: (item: T, index: number) => React.ReactNode;
  height: number | string;
  overscan?: number;
  onScrollEnd?: () => void;
  scrollEndThreshold?: number;
  gap?: number;
  getItemKey?: (item: T, index: number) => string | number;
}

function VirtualListInner<T>(
  {
    items,
    itemHeight,
    renderItem,
    height,
    overscan = 5,
    onScrollEnd,
    scrollEndThreshold = 100,
    gap = 0,
    getItemKey = (_, index) => index,
  }: VirtualListProps<T>,
  ref: React.Ref<HTMLDivElement>
) {
  const theme = useMantineTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const getHeight = useCallback(
    (index: number): number => {
      return typeof itemHeight === 'function' ? itemHeight(index) : itemHeight;
    },
    [itemHeight]
  );

  const itemPositions = useMemo(() => {
    const positions: { top: number; height: number }[] = [];
    let currentTop = 0;

    for (let i = 0; i < items.length; i++) {
      const h = getHeight(i);
      positions.push({ top: currentTop, height: h });
      currentTop += h + gap;
    }

    return positions;
  }, [items.length, getHeight, gap]);

  const totalHeight = useMemo(() => {
    if (itemPositions.length === 0) return 0;
    const last = itemPositions[itemPositions.length - 1];
    return last.top + last.height;
  }, [itemPositions]);

  const visibleRange = useMemo(() => {
    if (itemPositions.length === 0) {
      return { start: 0, end: 0 };
    }

    let start = 0;
    let end = items.length - 1;

    // Binary search for start
    let low = 0;
    let high = itemPositions.length - 1;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (itemPositions[mid].top + itemPositions[mid].height < scrollTop) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    start = Math.max(0, low - overscan);

    // Binary search for end
    low = 0;
    high = itemPositions.length - 1;
    const viewportBottom = scrollTop + containerHeight;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (itemPositions[mid].top < viewportBottom) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    end = Math.min(items.length - 1, low + overscan);

    return { start, end };
  }, [scrollTop, containerHeight, itemPositions, items.length, overscan]);

  const visibleItems = useMemo(() => {
    const result = [];
    for (let i = visibleRange.start; i <= visibleRange.end; i++) {
      result.push({
        item: items[i],
        index: i,
        style: {
          position: 'absolute' as const,
          top: itemPositions[i]?.top ?? 0,
          left: 0,
          right: 0,
          height: itemPositions[i]?.height ?? getHeight(i),
        },
      });
    }
    return result;
  }, [visibleRange, items, itemPositions, getHeight]);

  const handleScroll = useCallback(
    (position: { x: number; y: number }) => {
      setScrollTop(position.y);

      if (onScrollEnd && totalHeight - position.y - containerHeight < scrollEndThreshold) {
        onScrollEnd();
      }
    },
    [onScrollEnd, totalHeight, containerHeight, scrollEndThreshold]
  );

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerHeight(entry.contentRect.height);
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  return (
    <ScrollArea
      ref={ref}
      h={height}
      viewportRef={containerRef}
      onScrollPositionChange={handleScroll}
    >
      <Box style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems.map(({ item, index, style }) => (
          <Box key={getItemKey(item, index)} style={style}>
            {renderItem(item, index)}
          </Box>
        ))}
      </Box>
    </ScrollArea>
  );
}

const VirtualList = forwardRef(VirtualListInner) as <T>(
  props: VirtualListProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export default VirtualList;
